import { Button, Input, Select, useI18n } from '@upweo/ui';
import type { ProductStatus, ProductSummary } from '@upweo/types';
import { useMutation } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { ApiClientError } from '../lib/api-client.js';
import * as api from '../lib/api.js';
import { VariantEditor } from './VariantEditor.js';

interface Props {
  readonly onClose: () => void;
  readonly onSaved: () => void;
  /** Present = edit mode. Absent = create. */
  readonly product?: ProductSummary;
}

/**
 * Create or edit a product.
 *
 * Create also seeds the first variant and its stock; edit touches product
 * fields only. Price and stock live on the VARIANT, and editing those properly
 * needs a variant editor rather than a single-price shortcut that would
 * silently ignore products with more than one.
 */
export const ProductForm = ({ onClose, onSaved, product }: Props): React.JSX.Element => {
  const { t } = useI18n();
  const isEdit = product !== undefined;

  const [title, setTitle] = useState(product?.title ?? '');
  const [price, setPrice] = useState(product?.priceFrom ?? '19.99');
  const [sku, setSku] = useState('');
  const [quantity, setQuantity] = useState('0');
  const [status, setStatus] = useState<ProductStatus>(product?.status ?? 'ACTIVE');
  const [vendor, setVendor] = useState(product?.vendor ?? '');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      isEdit
        ? api.updateProduct(product.id, {
            title,
            status,
            // Empty string means "cleared", which is a null column, not ''.
            vendor: vendor.trim() === '' ? null : vendor,
          })
        : api.createProduct({
            title,
            // Money crosses the wire as a decimal string, never a float.
            price,
            status,
            quantity: Number(quantity),
            tags: [],
            ...(sku ? { sku } : {}),
            ...(vendor ? { vendor } : {}),
          }),
    onSuccess: onSaved,
    onError: (err: unknown) => {
      setError(
        err instanceof ApiClientError
          ? (err.error.fieldErrors?.map((f) => `${f.path}: ${f.message}`).join(', ') ??
              err.error.message)
          : 'Could not save the product',
      );
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setError(null);
    create.mutate();
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4 rounded-lg border bg-card p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-medium">{isEdit ? `Edit ${product.title}` : 'New product'}</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <label htmlFor="title" className="text-sm font-medium">
            Title
          </label>
          <Input
            id="title"
            required
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
            }}
            placeholder="Merino Scarf"
          />
          <p className="text-xs text-muted-foreground">
            The URL handle is generated from this if you don&apos;t set one.
          </p>
        </div>

        {/*
          Create-only. Price, stock and SKU belong to the VARIANT, and editing
          them here would silently target only the first one — wrong the moment
          a product has several. They reappear with the variant editor.
        */}
        {!isEdit && (
          <>
            <div className="space-y-1.5">
              <label htmlFor="price" className="text-sm font-medium">
                Price
              </label>
              <Input
                id="price"
                required
                inputMode="decimal"
                value={price}
                onChange={(e) => {
                  setPrice(e.target.value);
                }}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="quantity" className="text-sm font-medium">
                Starting stock
              </label>
              <Input
                id="quantity"
                type="number"
                min={0}
                value={quantity}
                onChange={(e) => {
                  setQuantity(e.target.value);
                }}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="sku" className="text-sm font-medium">
                SKU <span className="text-muted-foreground">(optional)</span>
              </label>
              <Input
                id="sku"
                value={sku}
                onChange={(e) => {
                  setSku(e.target.value);
                }}
              />
            </div>
          </>
        )}

        <div className="space-y-1.5">
          <label htmlFor="vendor" className="text-sm font-medium">
            Vendor <span className="text-muted-foreground">(optional)</span>
          </label>
          <Input
            id="vendor"
            value={vendor}
            onChange={(e) => {
              setVendor(e.target.value);
            }}
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="status" className="text-sm font-medium">
            Status
          </label>
          <Select
            id="status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as ProductStatus);
            }}
          >
            <option value="ACTIVE">{t('products.statusActive')}</option>
            <option value="DRAFT">{t('products.statusDraft')}</option>
          </Select>
        </div>
      </div>

      {error && (
        <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      <Button type="submit" disabled={create.isPending}>
        {create.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create product'}
      </Button>

      {/*
        Variants only exist once the product does, so this appears in edit mode
        only. Its rows save independently of this form — mixing them into one
        submit would make a single bad SKU reject the title change too.
      */}
      {isEdit && (
        <section className="space-y-3 border-t pt-4">
          <div>
            <h3 className="text-sm font-medium">{t('products.columnVariants')}</h3>
            <p className="text-xs text-muted-foreground">
              Price, SKU and stock live here. Each row saves on its own.
            </p>
          </div>
          <VariantEditor productId={product.id} />
        </section>
      )}
    </form>
  );
};
